import type {
  ReviewImageDescription,
  ReviewIssue,
  ReviewResult,
} from "./api";

const GENERIC_LOCATION_PREFIXES = new Set([
  "",
  "device",
  "device.apps",
  "canon",
  "game",
  "meta",
]);

export type ReviewProblemIssue = {
  title: string;
  severity: ReviewIssue["severity"];
  location: string | null;
};

export type ReviewProblemImage = {
  url: string;
  paths: string[];
  description: string;
  extractOk: boolean;
  issues: ReviewProblemIssue[];
};

export function parseReviewOutput(output: unknown): ReviewResult | null {
  if (!output || typeof output !== "object" || Array.isArray(output)) {
    return null;
  }
  const o = output as Record<string, unknown>;
  if (typeof o.summary !== "string") return null;
  return {
    ok: o.ok === true,
    summary: o.summary,
    findings: Array.isArray(o.findings)
      ? (o.findings as ReviewResult["findings"])
      : [],
    issues: Array.isArray(o.issues)
      ? (o.issues as ReviewResult["issues"])
      : [],
    images: Array.isArray(o.images)
      ? (o.images as ReviewResult["images"])
      : [],
    imageCount: typeof o.imageCount === "number" ? o.imageCount : 0,
    imageFailures: typeof o.imageFailures === "number" ? o.imageFailures : 0,
    model: typeof o.model === "string" ? o.model : "",
    notes: typeof o.notes === "string" ? o.notes : "",
  };
}

export function assetIdFromMediaUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const path = trimmed.includes("://")
      ? new URL(trimmed).pathname
      : trimmed.split("?")[0]!.split("#")[0]!;
    const match = /\/api\/media\/assets\/([^/]+)$/.exec(path);
    if (!match) return null;
    const id = decodeURIComponent(match[1]!).trim();
    return id || null;
  } catch {
    return null;
  }
}

export function pathsRelated(a: string, b: string): boolean {
  const x = a.trim();
  const y = b.trim();
  if (!x || !y) return false;
  if (x === y) return true;
  if (isPrefixPath(x, y) || isPrefixPath(y, x)) return true;
  const parentX = parentObjectPath(x);
  const parentY = parentObjectPath(y);
  return Boolean(
    parentX &&
      parentX === parentY &&
      /\[\d+\]/.test(parentX),
  );
}

export function imagesForLocation(
  location: string | null,
  images: ReviewImageDescription[],
): ReviewImageDescription[] {
  if (!location) return [];
  return images.filter((image) => pathsRelated(location, image.path));
}

export function collectReviewProblemImages(input: {
  issues: ReviewIssue[];
  images: ReviewImageDescription[];
}): ReviewProblemImage[] {
  const byUrl = new Map<
    string,
    {
      url: string;
      paths: Set<string>;
      description: string;
      extractOk: boolean;
      issues: ReviewProblemIssue[];
      issueKeys: Set<string>;
    }
  >();

  const take = (image: ReviewImageDescription) => {
    const url = image.url.trim();
    if (!url) return null;
    let row = byUrl.get(url);
    if (!row) {
      row = {
        url,
        paths: new Set<string>(),
        description: image.description,
        extractOk: image.ok,
        issues: [],
        issueKeys: new Set<string>(),
      };
      byUrl.set(url, row);
    }
    if (image.path) row.paths.add(image.path);
    if (!row.description && image.description) {
      row.description = image.description;
    }
    if (!image.ok) row.extractOk = false;
    return row;
  };

  for (const image of input.images) {
    if (!image.ok) take(image);
  }

  for (const issue of input.issues) {
    const matched = imagesForLocation(issue.location, input.images);
    for (const image of matched) {
      const row = take(image);
      if (!row) continue;
      const key = `${issue.severity}:${issue.title}:${issue.location ?? ""}`;
      if (row.issueKeys.has(key)) continue;
      row.issueKeys.add(key);
      row.issues.push({
        title: issue.title,
        severity: issue.severity,
        location: issue.location,
      });
    }
  }

  for (const image of input.images) {
    const row = byUrl.get(image.url.trim());
    if (row && image.path) row.paths.add(image.path);
  }

  return [...byUrl.values()]
    .map((row) => ({
      url: row.url,
      paths: [...row.paths],
      description: row.description,
      extractOk: row.extractOk,
      issues: row.issues,
    }))
    .sort(
      (a, b) =>
        problemRank(a) - problemRank(b) ||
        (a.paths[0] ?? a.url).localeCompare(b.paths[0] ?? b.url),
    );
}

export function rewriteStringDeep(
  value: unknown,
  from: string,
  to: string,
): unknown {
  if (!from || from === to) return value;
  if (typeof value === "string") {
    return value === from ? to : value.split(from).join(to);
  }
  if (Array.isArray(value)) {
    return value.map((item) => rewriteStringDeep(item, from, to));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = rewriteStringDeep(v, from, to);
    }
    return out;
  }
  return value;
}

function isPrefixPath(longer: string, shorter: string): boolean {
  if (GENERIC_LOCATION_PREFIXES.has(shorter)) return false;
  return longer.startsWith(`${shorter}.`) || longer.startsWith(`${shorter}[`);
}

function parentObjectPath(path: string): string {
  const stripped = path.replace(/\.[^.[\]]+$/, "");
  return stripped === path ? "" : stripped;
}

function problemRank(image: ReviewProblemImage): number {
  if (image.issues.some((issue) => issue.severity === "error")) return 0;
  if (image.issues.some((issue) => issue.severity === "warn")) return 1;
  if (!image.extractOk) return 2;
  return 3;
}
